/**
 * Abas da Outlive — alternativa ao accordion na PDP.
 *
 * O Liquid já entrega os painéis com [hidden] no estado correto, então a
 * primeira pintura sai certa mesmo antes deste módulo rodar. Aqui só entram
 * interação, animação e acessibilidade.
 *
 * Sem imports de @theme: o bloco carrega este arquivo direto, sem precisar
 * registrar nada no import map de snippets/scripts.liquid.
 */

const REDUZIDO = '(prefers-reduced-motion: reduce)';

class OutliveTabs extends HTMLElement {
  #abas = [];
  #paineis = [];

  connectedCallback() {
    this.#abas = Array.from(this.querySelectorAll('[data-otabs-tab]'));
    this.#paineis = Array.from(this.querySelectorAll('[data-otabs-panel]'));
    if (!this.#abas.length) return;

    this.#sincronizarInicial();
    this.addEventListener('click', this.#aoClicar);
    this.addEventListener('keydown', this.#aoTeclar);

    /* No editor de temas, selecionar a aba na barra lateral deve abri-la —
       senão o merchant edita um conteúdo que não consegue ver. */
    document.addEventListener('shopify:block:select', this.#aoSelecionar);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.#aoSelecionar);
  }

  /**
   * O estado inicial vem do Liquid — cada aba traz sua própria marcação de
   * "abrir ao carregar", porque um bloco filho não enxerga nem o índice nem as
   * settings do pai. Se o merchant marcar mais de uma, vale a primeira; e o
   * aria precisa refletir o que já está na tela.
   */
  #sincronizarInicial() {
    /* Modo "nenhuma aberta": o CSS já esconde tudo desde a primeira pintura,
       então aqui é só alinhar o DOM com o que a tela mostra. */
    if (this.dataset.initial === 'none') {
      this.#paineis.forEach((painel) => {
        painel.hidden = true;
      });
    }

    let ativa = -1;
    this.#paineis.forEach((painel, i) => {
      if (painel.hidden) return;
      if (ativa === -1) ativa = i;
      else painel.hidden = true;
    });

    this.#abas.forEach((aba, i) => {
      aba.setAttribute('aria-selected', String(i === ativa));
      aba.tabIndex = i === ativa || (ativa === -1 && i === 0) ? 0 : -1;
    });

    /* A partir daqui quem manda na visibilidade é o [hidden]. Desliga a regra
       de primeira pintura, senão nenhuma aba conseguiria abrir. */
    this.dataset.ready = 'true';
  }

  get #animar() {
    return this.dataset.animate === 'true' && !window.matchMedia(REDUZIDO).matches;
  }

  /** Só recolhe se a seção nasce sem nada aberto — aí recolher faz parte da linguagem. */
  get #recolhivel() {
    return this.dataset.collapsible === 'true';
  }

  #aoClicar = (evento) => {
    const aba = evento.target.closest('[data-otabs-tab]');
    if (!aba || !this.contains(aba)) return;
    const i = this.#abas.indexOf(aba);
    if (i < 0) return;

    const jaAtiva = aba.getAttribute('aria-selected') === 'true';
    this.abrir(jaAtiva && this.#recolhivel ? -1 : i);
  };

  #aoTeclar = (evento) => {
    const aba = evento.target.closest('[data-otabs-tab]');
    if (!aba || !this.contains(aba)) return;

    const i = this.#abas.indexOf(aba);
    const ultimo = this.#abas.length - 1;
    let destino = null;

    if (evento.key === 'ArrowRight' || evento.key === 'ArrowDown') destino = i >= ultimo ? 0 : i + 1;
    if (evento.key === 'ArrowLeft' || evento.key === 'ArrowUp') destino = i <= 0 ? ultimo : i - 1;
    if (evento.key === 'Home') destino = 0;
    if (evento.key === 'End') destino = ultimo;

    if (destino === null) return;
    evento.preventDefault();
    this.#abas[destino].focus();
    this.abrir(destino);
  };

  #aoSelecionar = (evento) => {
    const alvo = evento.target;
    if (!alvo || !this.contains(alvo)) return;
    const painel = alvo.closest('[data-otabs-panel]') || alvo.querySelector('[data-otabs-panel]');
    const i = this.#paineis.indexOf(painel);
    if (i >= 0) this.abrir(i);
  };

  /** @param {number} indice — -1 fecha tudo */
  abrir(indice) {
    this.#abas.forEach((aba, i) => {
      aba.setAttribute('aria-selected', String(i === indice));
      /* Só a aba ativa fica no fluxo do Tab; as demais respondem às setas.
         É o padrão de tablist e evita 6 paradas de teclado seguidas. */
      aba.tabIndex = i === indice || (indice === -1 && i === 0) ? 0 : -1;
    });

    this.#paineis.forEach((painel, i) => {
      const abrindo = i === indice;
      const estavaVisivel = !painel.hidden;
      painel.hidden = !abrindo;
      if (abrindo && !estavaVisivel && this.#animar) this.#animarEntrada(painel);
    });

    this.dispatchEvent(new CustomEvent('outlive-tabs:change', { detail: { index: indice }, bubbles: true }));
  }

  /**
   * Altura de 0 até a natural, mais fade. Anima a altura de propósito: sem
   * isso, painéis de tamanhos diferentes fazem o bloco pular na troca — e logo
   * abaixo dele fica o botão de comprar.
   */
  #animarEntrada(painel) {
    const alvo = painel.scrollHeight;
    if (!alvo || typeof painel.animate !== 'function') return;

    const animacao = painel.animate(
      [
        { height: '0px', opacity: 0, transform: 'translateY(6px)' },
        { height: `${alvo}px`, opacity: 1, transform: 'none' },
      ],
      { duration: Number(this.dataset.duration) || 320, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
    );

    /* overflow some só durante a animação: cortar depois quebraria conteúdo
       que vaza de propósito, como sombra ou tooltip. */
    painel.style.overflow = 'hidden';
    animacao.finished
      .catch(() => {})
      .finally(() => {
        painel.style.overflow = '';
      });
  }
}

if (!customElements.get('outlive-tabs')) customElements.define('outlive-tabs', OutliveTabs);
